import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Fingerprint } from 'lucide-react';
import toast from 'react-hot-toast';

import { useAuth } from '../auth/AuthProvider.jsx';
import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Button } from '../components/ui/Button.jsx';
import { Input } from '../components/ui/Input.jsx';
import { env } from '../lib/env.js';
import { t } from '../lib/i18n.js';

function readableError(err) {
  if (!(err instanceof ApiError)) return t('login.error.generic');
  switch (err.code) {
    case 'INVALID_CREDENTIALS':
      return t('login.error.invalid');
    case 'MAINTENANCE_MODE':
      return t('login.error.maintenance');
    case 'FORBIDDEN':
      return t('login.error.locked');
    default:
      return err.message || t('login.error.generic');
  }
}

export function LoginPage() {
  const { status, login, completePasskeyLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const supportsPasskey = browserSupportsWebAuthn();

  // The kiosk QR landing redirects here with the linked email so the customer
  // only has to type their password (and pass MFA/passkey if enabled).
  const prefilledEmail = (location.state && location.state.email) || '';

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm({
    defaultValues: { email: prefilledEmail, password: '' }
  });

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (status === 'authenticated') {
    const dest = (location.state && location.state.from) || '/';
    return <Navigate to={dest} replace />;
  }

  const destination = () => (location.state && location.state.from) || '/';

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      await login({ email: values.email.trim(), password: values.password });
      navigate(destination(), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'MFA_REQUIRED') {
        const methods = (err.details && err.details.methods) || ['totp'];
        navigate('/login/mfa', {
          replace: true,
          state: {
            email: values.email.trim(),
            password: values.password,
            methods,
            from: location.state && location.state.from
          }
        });
        return;
      }
      if (err instanceof ApiError && err.code === 'VALIDATION_ERROR' && Array.isArray(err.details)) {
        for (const d of err.details) {
          const field = Array.isArray(d.path) ? d.path[0] : d.path;
          if (field === 'email' || field === 'password') {
            setError(field, { type: 'server', message: d.message });
          }
        }
        return;
      }
      toast.error(readableError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onPasskey = async () => {
    if (!supportsPasskey) {
      toast.error(t('login.passkey.unsupported'));
      return;
    }
    setPasskeyBusy(true);
    try {
      const { options, challengeId } = await authApi.passkeyLoginStart();
      const response = await startAuthentication({ optionsJSON: options });
      await completePasskeyLogin({ challengeId, response });
      navigate(destination(), { replace: true });
    } catch (err) {
      if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        toast.error(t('login.passkey.cancelled'));
        return;
      }
      if (err instanceof ApiError) {
        toast.error(err.code === 'MFA_INVALID' || err.code === 'INVALID_CREDENTIALS'
          ? t('login.passkey.failed')
          : readableError(err));
        return;
      }
      toast.error(t('login.passkey.failed'));
    } finally {
      setPasskeyBusy(false);
    }
  };

  const busy = submitting || passkeyBusy;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-10">
      <div className="w-full max-w-[380px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-surface-elevated shadow-card">
            <img src="/favicon.png" alt={env.appName} className="h-7 w-7" />
          </span>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{t('login.title')}</h1>
          <p className="mt-1.5 text-sm text-ink-muted">{t('login.subtitle')}</p>
        </div>

        <div className="rounded-2xl border border-border bg-surface-elevated p-6 shadow-card sm:p-7">
          <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Input
              label={t('login.email')}
              type="email"
              autoComplete="username webauthn"
              autoFocus={!prefilledEmail}
              disabled={busy}
              error={errors.email && (errors.email.message || 'Email non valida')}
              {...register('email', {
                required: 'Email richiesta',
                pattern: { value: EMAIL_RE, message: 'Formato email non valido' }
              })}
            />
            <Input
              label={t('login.password')}
              type="password"
              autoComplete="current-password"
              autoFocus={Boolean(prefilledEmail)}
              disabled={busy}
              error={errors.password && (errors.password.message || 'Password richiesta')}
              {...register('password', { required: 'Password richiesta', minLength: { value: 1, message: 'Password richiesta' } })}
            />
            <Button type="submit" loading={submitting} disabled={passkeyBusy} className="mt-1 w-full">
              {submitting ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          {supportsPasskey && (
            <>
              <div className="my-5 flex items-center gap-3 text-xs uppercase tracking-wider text-ink-faint">
                <span className="h-px flex-1 bg-border" />
                {t('login.or')}
                <span className="h-px flex-1 bg-border" />
              </div>
              <Button
                type="button"
                variant="secondary"
                className="w-full"
                loading={passkeyBusy}
                disabled={submitting}
                onClick={onPasskey}
              >
                {!passkeyBusy && <Fingerprint className="h-4 w-4" />}
                {passkeyBusy ? t('login.passkey.busy') : t('login.passkey')}
              </Button>
              <p className="mt-2.5 text-center text-xs text-ink-muted">{t('login.passkeyHint')}</p>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-ink-faint">{env.appName} &middot; area riservata</p>
      </div>
    </div>
  );
}
